// AddressForm.tsx — SINGLE reusable component (for Add & Edit)
// - Pure UI only; no routing or page shell
// - Parent owns Formik (initialValues, validation, submit)
// - Uses your UI primitives: UIInput, UIReactSelect, UICheckbox
// - Includes Google Places Autocomplete for addressLine1 internally (optional)
// - Exact field names taken from your SignageAddAddress / SignageEditAddress files

import React, { useRef, useEffect } from "react";
import type { FormikProps } from "formik";
import { UIInput, UIInputDisable, UIReactSelect, UICheckbox } from "components/Inputfields";
import { useLoadScript } from "@react-google-maps/api";
import AddressAutocomplete from "./AddressAutocomplete";
import { US_STATES, getUsStateByCode, getUsStateByName } from "constants/usStates";

export type AddressValues = {
  label_of_address: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
};

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

export default function AddressForm({
  formik,
  googleMapsApiKey,
  onCancel,
  submitLabel = "Save",
}: {
  formik: FormikProps<AddressValues>;
  googleMapsApiKey?: string;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const libraries: Array<"places"> = ["places"];
  const { isLoaded } = useLoadScript({ googleMapsApiKey: googleMapsApiKey || "", libraries });

  const hasGoogleCoordinates =
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };

  const selectedManualStateCode =
    formik.values.stateCode || getUsStateByName(formik.values.state)?.value || "";

  const handleManualStateChange = (value: string) => {
    const selectedState = getUsStateByCode(value);
    formik.setFieldValue("stateCode", selectedState?.value || "");
    formik.setFieldValue("state", selectedState?.name || "");
  };

  // Default phone to 10 zeros on first mount if empty
  useEffect(() => {
    if (!((formik.values as any)?.phone ?? "").toString().trim()) {
      formik.setFieldValue("phone", "0000000000", false);
    }
    // run once
    // eslint-disable-next-line
  }, []);

  return (
    <form onSubmit={formik.handleSubmit} className="container-fluid">
      {/* Address Details */}
      <div className="rounded-4 border bg-white box-shadow-datatables p-3 p-md-4 mb-5 mt-4">
        <div className="d-flex align-items-center mb-5">
          <span className="chip chip-warning me-2"><i className="bi bi-geo-alt-fill" /></span>
          <h6 className="mb-0 fs-19 roboto-new" style={{ color: "#101828" }}>Address Details</h6>
        </div>
        <div className="row g-3 g-md-4 mt-4 rounded-4 ms-1" id="row-overlap">
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="label_of_address" label="Label of address" placeholder="e.g., Home, Office, etc." />
          </div>
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="firstName" label="First Name" placeholder="Enter first name" />
          </div>
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="lastName" label="Last Name"  placeholder="Enter last name" />
          </div>
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="phone" label="Phone Number" placeholder="+1 (555) 123-4567" />
          </div>
          {/* 
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="company" label="Company" placeholder="Company (optional)" />
          </div> */}
        </div>
      </div>

      {/* Location Details */}
      <div className="rounded-4 border bg-white box-shadow-datatables p-3 p-md-4 mb-4">
        <div className="d-flex align-items-center mb-5">
          <span className="chip chip-pink me-2"><i className="bi bi-house-door-fill" /></span>
          <h6 className="mb-0 fs-19 roboto-new" style={{ color: "#101828" }}>Location Details</h6>
        </div>

        <div className="row g-3 g-md-4 mt-4 ms-1">
          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <label className="form-label">Address Line 1 <span className="text-danger">*</span> </label>

            <AddressAutocomplete
              id="addressLine1"
              name="addressLine1"
              value={(formik.values as any).addressLine1}
              onChange={handleAddressLine1Change}
              onSelect={(picked) => {
                formik.setFieldValue("addressLine1", picked.addressLine1);
                formik.setFieldValue("city", picked.city);
                formik.setFieldValue("cityCode", picked.cityCode);
                formik.setFieldValue("state", picked.state);
                formik.setFieldValue("stateCode", picked.stateCode);
                formik.setFieldValue("zipcode", picked.zipcode);
                formik.setFieldValue("country", picked.country);
                formik.setFieldValue("countryCode", picked.countryCode);
                formik.setFieldValue("latitude", picked.latitude);
                formik.setFieldValue("longitude", picked.longitude);
                formik.setFieldTouched("addressLine1", false, false);
                formik.setFieldError("addressLine1", undefined);
                clearAddressDetailErrors();
              }}
              className={`form-control ${((formik.touched as any).addressLine1 && (formik.errors as any).addressLine1) ? "is-invalid" : ""}`}
              placeholder="Enter a location"
              error={(formik.touched as any).addressLine1 && (formik.errors as any).addressLine1 ? ((formik.errors as any).addressLine1 as string) : undefined}
              autoComplete="new-password"
            />
          </div>

          <div className="col-12 col-md-6 roboto-new" id="row-overlap">
            <UIInput formik={formik as any} name="addressLine2" label="Address Line 2" placeholder="Apartment, suite, etc. (optional)" />
          </div>

          <div className="col-12 col-md-4 roboto-new" id="row-overlap">
            <UIInputDisable
              formik={formik as any}
              name="city"
              label="City"
              required
              placeholder="City"
              disabled={hasGoogleCoordinates}
              autocomplete="new-password"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <div className="col-12 col-md-4 roboto-new" id="row-overlap">
            <label className="form-label">
              State <span className="text-danger">*</span>
            </label>
            {hasGoogleCoordinates ? (
              <input
                type="text"
                name="state"
                className="form-control"
                placeholder="State"
                value={formik.values.state}
                disabled
                autoComplete="new-password"
                autoCorrect="off"
                spellCheck={false}
              />
            ) : (
              <select
                id="stateCode"
                name="state"
                className={`form-select ${formik.touched.state && formik.errors.state ? "is-invalid" : ""}`}
                value={selectedManualStateCode}
                onChange={(e) => handleManualStateChange(e.target.value)}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
              >
                <option value="">-- Select State --</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
            )}
            {formik.touched.state && formik.errors.state && (
              <div className="invalid-feedback d-block">{formik.errors.state as string}</div>
            )}
          </div>
          <div className="col-12 col-md-4 roboto-new" id="row-overlap">
            <UIInputDisable
              formik={formik as any}
              name="zipcode"
              label="Zip Code"
              required
              placeholder="Zip code"
              disabled={hasGoogleCoordinates}
              autocomplete="new-password"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>

          <div className="col-12 col-md-4 roboto-new" id="row-overlap">
            <UIInputDisable
              formik={formik as any}
              name="country"
              label="Country"
              required
              placeholder="United States"
              disabled
              autocomplete="new-password"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-end gap-2 py-4">
        {onCancel && (
          <button type="button" className="btn back-btn surface-btn fw-bold fs-14" onClick={onCancel}>Cancel</button>
        )}
        <button type="submit" className="btn save-btn surface-btn fw-bold fs-14" disabled={formik.isSubmitting}>{submitLabel}</button>
      </div>

      {/* Local styles */}
      <style>{`
        .chip { display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px; border-radius:12px; }
        .chip i { font-size:18px; }
        .chip-warning { background:#FFEAD1; color:#F59E0B; }
        .chip-pink { background:#FFE1F0; color:#E11D48; }
      `}</style>
    </form>
  );
}

